import { Observable } from 'rxjs';
import { DataAggregation } from '../../dataModel';
import { DataType } from '../../dataModel/DataType';
import { VisualFieldTarget } from '../VisualFieldTarget';
import { VisualFilter } from '../VisualFilter';
import { ChunkResult, RowsResult, VisualDataQueryColumn, VisualDataQueryColumnCollection, VisualDataQueryTable, VisualDataResource } from './VisualDataSource';
import { Predicate, PredicateType } from '../../utils/Predicate';
import { VisualField } from '../VisualField';
import { ExecuteDataQueryElementUsage, SchemaFieldTargetDto, SortDirection } from 'src/app/services/api.services';

export abstract class VisualDataAdapter {
  constructor(public readonly dataTable: VisualDataQueryTable) { }

  protected shouldUseSeparateQuery(r: VisualDataAdapter.RequestBase) {
    return !this.dataTable || this.dataTable.exceededRowLimits;
  }

  get request() { return this.dataTable.request; }
  get columns() { return this.dataTable.columns; }

  get totalRows() { return this.dataTable.totalRows; }
  get exceededRowLimits() { return this.dataTable.exceededRowLimits; }

  get rowsPerChunk() { return this.dataTable.rowsPerChunk; }
  get chunks() { return this.dataTable.chunks; }

  chunk(r: VisualDataAdapter.ChunkRequest): VisualDataResource<ChunkResult> {
    if (!r.filters?.length) {
      return this.dataTable.chunk(r.chunkIndex, {
        order: this.toResourceOrder(r.order),
        skip: r.skip,
        take: r.take,
        abortSignal: r.abortSignal,
        debugName: r.debugName,
      });
    }
    else {
      throw new Error('not implemented');
    }
  }

  rows(r: VisualDataAdapter.RowsRequest): VisualDataResource<RowsResult> {
    if (!r.filters?.length) {
      return this.dataTable.rows({
        order: this.toResourceOrder(r.order),
        skip: r.skip,
        take: r.take,
        abortSignal: r.abortSignal,
        debugName: r.debugName,
      });
    }
    else {
      throw new Error('not implemented');
    }
  }
  
  private toResourceOrder(orderList: VisualDataAdapter.OrderByList) {
    return orderList?.map(o => o)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(o => ({
        columnIndex: this.dataTable.columns.get(o.target, true).index,
        sort: o.sortDirection ?? SortDirection.Ascending
      }));
  }


  distinctValues(r: VisualDataAdapter.DistinctValuesRequest): VisualDataResource<RowsResult> {
    return this.distinctRows({
      abortSignal: r.abortSignal,
      debugName: [
        this.dataTable.request.debugName, 
        `distinctValues([${r.field.target.formatLongTitle('.')}])`
      ].join('.'),
      ...r,
      fields: [r.field]
    });
  }

  distinctRows(r: VisualDataAdapter.DistinctRowsRequest): VisualDataResource<RowsResult> {
    let fields: VisualDataAdapter.FieldCalculation[] = [];
    let measures: VisualDataAdapter.Calculation[] = [];
    r.fields.forEach(f => this.getDistinctValueCalculation(fields, measures, f));

    return this.calculateImpl({
      abortSignal: r.abortSignal,
      debugName: [
        this.dataTable.request.debugName, 
        `distinctRows([${r.fields.map(x => x.target.formatLongTitle('.')).join('], ')}])`
      ].join('.'),
      ...r,
      fields, 
      measures
    }, rs => ({
      ...rs,
      columns: new VisualDataQueryColumnCollection(
        ...r.fields.map((f, i) => {
          let name = fields[i].name.toLowerCase();
          let c = rs.columns.find(x => x.name.toLowerCase() == name);
          return {
            ...c,
            index: i,
            target: this.targetOf(f)
          };
        }))
    }));
  }

  aggregateColumn(r: VisualDataAdapter.AggregateColumnRequest): VisualDataResource<RowsResult> {
    return this.aggregateColumns({
      abortSignal: r.abortSignal,
      debugName: [
        this.dataTable.request.debugName,
        `aggregateColumn([${r.aggregation.target.formatLongTitle('.')}])`
      ].join('.'),
      ...r,
      aggregations: [r.aggregation]
    });
  }

  aggregateColumns(r: VisualDataAdapter.AggregateColumnsRequest): VisualDataResource<RowsResult> {
    let fields: VisualDataAdapter.FieldCalculation[] = [];
    let measures: VisualDataAdapter.Calculation[] = [];
    r.aggregations.forEach(a => this.getAggregationCalculation(fields, measures, a, a.aggregation));

    return this.calculateImpl({
      abortSignal: r.abortSignal,
      debugName: [
        this.dataTable.request.debugName, 
        `aggregateColumns([${r.aggregations.map(x => x.target.formatLongTitle('.')).join('], ')}])`
      ].join('.'),
      ...r,
      fields, 
      measures,
    }, rs => ({
      ...rs,
      columns: new VisualDataQueryColumnCollection(
        ...r.aggregations.map((a, i) => {
          let name = measures[i].name.toLowerCase();
          let c = rs.columns.find(x => x.name.toLowerCase() == name);
          return {
            ...c,
            index: i,
            target: this.targetOf(a.target)
          };
        }))
    }));
  }

  matchingValues(r: VisualDataAdapter.MatchingValuesRequest): VisualDataResource<RowsResult> {
    return this.distinctRows({
      abortSignal: r.abortSignal,
      debugName: [
        this.dataTable.request.debugName,
        `matchingValues([${r.field.target.formatLongTitle('.')}])`
      ].join('.'),
      ...r,
      fields: [r.field],
      filters: [
        ...(r.filters ?? []),
        this.getMatchingValuesPredicate(r.field, r.searchText)
      ]
    });
  }

  calculate(r: VisualDataAdapter.CalculateRequest): VisualDataResource<RowsResult> {
    return this.calculateImpl(r, null);
  }

  protected abstract calculateImpl(
    r: VisualDataAdapter.CalculateRequest,
    resultTransform?: (r: RowsResult) => RowsResult): VisualDataResource<RowsResult>;


  protected abstract getDistinctValueCalculation(
    fields: VisualDataAdapter.FieldCalculation[],
    measures: VisualDataAdapter.Calculation[],
    f: VisualDataAdapter.Field
  );

  protected abstract getAggregationCalculation(
    fields: VisualDataAdapter.FieldCalculation[],
    measures: VisualDataAdapter.Calculation[],
    f: VisualDataAdapter.Field, 
    aggregation: DataAggregation
  );

  protected getMatchingValuesPredicate(
    f: VisualDataAdapter.Field,
    searchText: string
  ): Predicate<VisualDataAdapter.Field> {
    return {
      target: f,
      type: PredicateType.Advanced,
      logicalOperator: Predicate.LogicalOperator.And,
      conditions: [{
        type: Predicate.ConditionType.Contains,
        value: searchText
      }]
    };
  }

  protected targetOf(f: VisualField | VisualFieldTarget | VisualDataAdapter.Field): VisualFieldTarget {
    return (f instanceof VisualField) ? f.target 
         : (f instanceof VisualFieldTarget) ? f
         : (f.target instanceof VisualField ? f.target.target : f.target);
  }
}

export namespace VisualDataAdapter {
  export type Field = {
    readonly target: VisualField | VisualFieldTarget;
  }

  export type FieldFilter = Predicate<Field>;
  
  export type Aggregation = Field & {
    readonly aggregation: DataAggregation | null;
  }

  export type OrderBy = Field & {
    readonly sortOrder: number;
    readonly sortDirection: SortDirection;
  }

  export type Calculation = {
    readonly name?: string;
    readonly expr: string;
    readonly dataType?: DataType;
    readonly return?: boolean;
  }

  export type FieldCalculation = Calculation & {
    readonly grouping?: boolean;
  }

  export type AggregationList = ReadonlyArray<Aggregation>;
  export type FieldList = ReadonlyArray<Field>;
  export type OrderByList = ReadonlyArray<OrderBy>;
  export type CalculationList = ReadonlyArray<Calculation>;
  export type FieldCalculationList = ReadonlyArray<FieldCalculation>;

  export interface RequestBase {
    readonly debugName?: string;
    readonly filters?: ReadonlyArray<Predicate<Field>>;
    readonly order?: OrderByList;
    readonly skip?: number;
    readonly take?: number;
    readonly abortSignal?: Observable<void>;
    readonly noCache?: boolean;
  }

  export interface ChunkRequest extends RequestBase {
    readonly chunkIndex: number;
  }

  export interface RowsRequest extends RequestBase {
  }

  export interface DistinctValuesRequest extends RequestBase {
    readonly field: Field;
  }

  export interface DistinctRowsRequest extends RequestBase {
    readonly fields: FieldList;
  }

  export interface AggregateColumnRequest extends RequestBase {
    readonly aggregation: Aggregation;
  }

  export interface AggregateColumnsRequest extends RequestBase {
    readonly aggregations: AggregationList;
  }

  export interface MatchingValuesRequest extends RequestBase {
    readonly field: Field;
    readonly searchText: string;
  }

  export type CalculateRequest = RequestBase & ({
    readonly fields: FieldCalculationList;
    readonly measures?: CalculationList;
  } | {
    readonly fields?: FieldCalculationList;
    readonly measures: CalculationList;
  });
}
